import {
  SupportCase,
  SupportQueue,
  SupportQueueId,
  FinancialCalculationEscalation,
  KnowledgeArticle,
  KnownIssue,
  KnowledgeGap,
  SupportMacro,
  VoiceOfCustomerTheme,
  CSATResponse,
  NPSMetricData,
  SupportPriority,
  SupportDomain
} from './supportTypes';
import {
  MOCK_SUPPORT_QUEUES,
  MOCK_SUPPORT_CASES,
  MOCK_FINANCIAL_ESCALATIONS,
  MOCK_KNOWLEDGE_ARTICLES,
  MOCK_KNOWN_ISSUES,
  MOCK_KNOWLEDGE_GAPS,
  MOCK_SUPPORT_MACROS,
  MOCK_VOC_THEMES,
  MOCK_CSAT_RESPONSES,
  MOCK_NPS_DATA
} from './mockSupportStore';

export interface SupportAuditEntry {
  id: string;
  timestamp: string;
  actor: string;
  action:
    | 'CASE_VIEWED'
    | 'CASE_CREATED'
    | 'MESSAGE_SENT'
    | 'SENSITIVE_CONTEXT_ACCESSED'
    | 'FINANCIAL_ESCALATION_CREATED'
    | 'REFUND_REQUESTED'
    | 'STATUS_CHANGED'
    | 'MACRO_APPLIED'
    | 'AI_SUGGESTION_ACCEPTED';
  casePublicId: string;
  details: string;
}

class SupportEngineService {
  private cases: SupportCase[] = [...MOCK_SUPPORT_CASES];
  private queues: SupportQueue[] = [...MOCK_SUPPORT_QUEUES];
  private financialEscalations: FinancialCalculationEscalation[] = [...MOCK_FINANCIAL_ESCALATIONS];
  private articles: KnowledgeArticle[] = [...MOCK_KNOWLEDGE_ARTICLES];
  private knownIssues: KnownIssue[] = [...MOCK_KNOWN_ISSUES];
  private knowledgeGaps: KnowledgeGap[] = [...MOCK_KNOWLEDGE_GAPS];
  private macros: SupportMacro[] = [...MOCK_SUPPORT_MACROS];
  private vocThemes: VoiceOfCustomerTheme[] = [...MOCK_VOC_THEMES];
  private csatResponses: CSATResponse[] = [...MOCK_CSAT_RESPONSES];
  private npsData: NPSMetricData = { ...MOCK_NPS_DATA };
  private auditLog: SupportAuditEntry[] = [
    {
      id: 'aud_001',
      timestamp: '2026-08-25T01:45:00Z',
      actor: 'David Sterling (Financial Specialist)',
      action: 'FINANCIAL_ESCALATION_CREATED',
      casePublicId: 'YP-CASE-84920',
      details: 'Created Financial Escalation FCE-9382 for Scottish 2026/27 PAYE review.'
    },
    {
      id: 'aud_002',
      timestamp: '2026-08-25T02:40:00Z',
      actor: 'Claire Fontaine (Billing Ops)',
      action: 'REFUND_REQUESTED',
      casePublicId: 'YP-CASE-84921',
      details: 'Issued €58.00 B2B VAT correction credit memo via Stripe connector.'
    },
    {
      id: 'aud_003',
      timestamp: '2026-08-25T03:00:00Z',
      actor: 'Sophie Weber (Privacy Coordinator)',
      action: 'STATUS_CHANGED',
      casePublicId: 'YP-CASE-84923',
      details: 'Routed GDPR Art. 17 erasure request to Prompt 22 Legal DPO desk.'
    }
  ];

  // Cases methods
  public getCases(): SupportCase[] {
    return [...this.cases];
  }

  public getCaseById(idOrPublicId: string): SupportCase | undefined {
    return this.cases.find(c => c.id === idOrPublicId || c.publicId === idOrPublicId);
  }

  public createCase(input: {
    customerUserId?: string;
    customerName: string;
    customerEmail: string;
    customerPlan?: 'FREE' | 'PRO_MONTHLY' | 'PRO_ANNUAL' | 'BUSINESS' | 'ENTERPRISE_API';
    market: string;
    locale: string;
    language: string;
    domain: SupportDomain;
    category: string;
    subcategory?: string;
    subject: string;
    message: string;
    developerRequestId?: string;
    billingTransactionRef?: string;
  }): SupportCase {
    const randomNum = Math.floor(10000 + Math.random() * 90000);
    const publicId = `YP-CASE-${randomNum}`;
    const id = `case_${Date.now()}`;

    // Rule-based routing & priority
    let assignedQueue: SupportQueueId = 'general-support';
    let priority: SupportPriority = 'P2_NORMAL';

    if (input.domain === 'CALCULATORS' || input.domain === 'TAX' || input.domain === 'SALARY') {
      assignedQueue = 'financial-calculations';
      priority = 'P1_HIGH';
    } else if (input.domain === 'BILLING' || input.domain === 'PAYMENT' || input.domain === 'REFUND') {
      assignedQueue = 'billing-payments';
      priority = 'P1_HIGH';
    } else if (input.domain === 'DEVELOPER_API') {
      assignedQueue = 'developer-support';
      priority = 'P1_HIGH';
    } else if (input.domain === 'PRIVACY') {
      assignedQueue = 'privacy-rights';
      priority = 'P2_NORMAL';
    } else if (input.domain === 'SECURITY') {
      assignedQueue = 'security-routing';
      priority = 'P0_CRITICAL';
    } else if (input.domain === 'ACCESSIBILITY') {
      assignedQueue = 'accessibility-support';
      priority = 'P2_NORMAL';
    } else if (input.domain === 'COMPLIANCE') {
      assignedQueue = 'compliance-escalations';
      priority = 'P1_HIGH';
    }

    const now = new Date().toISOString();
    const firstResponseDue = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
    const resolutionDue = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

    const newCase: SupportCase = {
      id,
      publicId,
      customerUserId: input.customerUserId || 'usr_guest_intake',
      customerName: input.customerName,
      customerEmail: input.customerEmail,
      customerPlan: input.customerPlan || 'FREE',
      market: input.market,
      locale: input.locale,
      language: input.language,
      channel: 'WEB_FORM',
      domain: input.domain,
      category: input.category,
      subcategory: input.subcategory || 'General Inquiry',
      subject: input.subject,
      priority,
      status: 'NEW',
      assignedQueue,
      createdAt: now,
      firstResponseDueAt: firstResponseDue,
      resolutionDueAt: resolutionDue,
      slaStatus: 'ON_TRACK',
      developerRequestId: input.developerRequestId,
      billingTransactionRef: input.billingTransactionRef,
      tags: [input.domain, input.market],
      messages: [
        {
          id: `msg_${Date.now()}`,
          caseId: id,
          senderType: 'CUSTOMER',
          senderName: input.customerName,
          senderEmail: input.customerEmail,
          content: input.message,
          timestamp: now
        }
      ]
    };

    this.cases.unshift(newCase);

    this.auditLog.unshift({
      id: `aud_${Date.now()}`,
      timestamp: now,
      actor: input.customerName,
      action: 'CASE_CREATED',
      casePublicId: publicId,
      details: `New customer case created via Web/In-App Form. Domain: ${input.domain}`
    });

    return newCase;
  }

  public addMessage(
    caseId: string,
    senderType: 'AGENT' | 'CUSTOMER' | 'INTERNAL_NOTE' | 'SYSTEM',
    senderName: string,
    content: string
  ): SupportCase | undefined {
    const targetCase = this.cases.find(c => c.id === caseId || c.publicId === caseId);
    if (!targetCase) return undefined;

    const newMessage = {
      id: `msg_${Date.now()}`,
      caseId: targetCase.id,
      senderType,
      senderName,
      content,
      timestamp: new Date().toISOString()
    };

    targetCase.messages.push(newMessage);

    if (senderType === 'AGENT') {
      if (!targetCase.firstResponseAt) {
        targetCase.firstResponseAt = new Date().toISOString();
      }
      targetCase.status = 'WAITING_FOR_CUSTOMER';
    } else if (senderType === 'CUSTOMER') {
      targetCase.status = 'OPEN';
    }

    this.auditLog.unshift({
      id: `aud_${Date.now()}`,
      timestamp: new Date().toISOString(),
      actor: senderName,
      action: 'MESSAGE_SENT',
      casePublicId: targetCase.publicId,
      details: `${senderType} message added: ${content.substring(0, 60)}...`
    });

    return { ...targetCase };
  }

  public updateCaseStatus(caseId: string, status: SupportCase['status'], actor: string): SupportCase | undefined {
    const targetCase = this.cases.find(c => c.id === caseId || c.publicId === caseId);
    if (!targetCase) return undefined;

    targetCase.status = status;
    if (status === 'RESOLVED') {
      targetCase.resolvedAt = new Date().toISOString();
      targetCase.slaStatus = 'MET';
    } else if (status === 'CLOSED') {
      targetCase.closedAt = new Date().toISOString();
    }

    this.auditLog.unshift({
      id: `aud_${Date.now()}`,
      timestamp: new Date().toISOString(),
      actor,
      action: 'STATUS_CHANGED',
      casePublicId: targetCase.publicId,
      details: `Status changed to ${status}`
    });

    return { ...targetCase };
  }

  // Financial calculation escalation
  public createFinancialEscalation(
    caseId: string,
    tool: FinancialCalculationEscalation['tool'],
    market: string,
    jurisdiction: string,
    rulesetVersion: string,
    formulaVersion: string,
    inputSnapshot: Record<string, string | number | boolean>,
    systemOutputSnapshot: Record<string, string | number | boolean>,
    customerClaim: string,
    actorName: string
  ): FinancialCalculationEscalation {
    const escalationId = `FCE-${Math.floor(1000 + Math.random() * 9000)}`;
    const newEscalation: FinancialCalculationEscalation = {
      id: escalationId,
      caseId,
      tool,
      market,
      jurisdiction,
      rulesetVersion,
      formulaVersion,
      inputSnapshot,
      systemOutputSnapshot,
      customerClaimedDiscrepancy: customerClaim,
      status: 'RECEIVED',
      investigatorNotes: `Intake by ${actorName}. Scenario assigned for statutory ruleset comparison.`,
      validatorName: actorName,
      verifiedAt: new Date().toISOString()
    };

    this.financialEscalations.unshift(newEscalation);

    // Link to case
    const targetCase = this.cases.find(c => c.id === caseId || c.publicId === caseId);
    if (targetCase) {
      targetCase.financialEscalationId = escalationId;
      targetCase.status = 'ESCALATED';
    }

    this.auditLog.unshift({
      id: `aud_${Date.now()}`,
      timestamp: new Date().toISOString(),
      actor: actorName,
      action: 'FINANCIAL_ESCALATION_CREATED',
      casePublicId: targetCase?.publicId || caseId,
      details: `Created Financial Escalation ${escalationId} for ${tool} [${jurisdiction}]`
    });

    return newEscalation;
  }

  // AI Copilot Suggestion Generator
  public generateAICopilotSuggestion(caseItem: SupportCase): {
    suggestedReply: string;
    confidence: number;
    sources: { title: string; slug: string; ruleset?: string }[];
    recommendedAction: string;
    isHighRisk: boolean;
  } {
    if (caseItem.domain === 'CALCULATORS' || caseItem.domain === 'TAX') {
      return {
        suggestedReply: `Hello ${caseItem.customerName},\n\nThank you for sharing your exact calculation details. We have verified our Scottish PAYE ruleset (UK_HMRC_2026_V1.2). For the 2026/27 tax year, the Intermediate tax band applies between £26,800 and £43,662 at 21%. We have cross-referenced your Plan 2 Student Loan 9% threshold (£27,295). Our actuarial team has confirmed your scenario and our live engine reflects the statutory HMRC schedule.\n\nWarm regards,\nYamooPay Support`,
        confidence: 0.94,
        sources: [
          {
            title: 'Understanding Scottish Income Tax vs UK Mainland Rates (2026/27)',
            slug: 'understanding-scottish-income-tax-vs-uk-mainland',
            ruleset: 'UK_HMRC_2026_V1.2'
          }
        ],
        recommendedAction: 'Apply verified Scottish Tax macro or escalate to Prompt 23 Actuarial Desk if dispute persists.',
        isHighRisk: true // Requires human review before sending!
      };
    }

    if (caseItem.domain === 'BILLING') {
      return {
        suggestedReply: `Bonjour ${caseItem.customerName},\n\nNous avons bien vérifié votre numéro de TVA intracommunautaire. Votre compte Pro a été configuré en régime d'autoliquidation européenne (B2B Hors Taxe). La facture rectificative est accessible dans votre portail de facturation.\n\nCordialement,\nL'équipe Facturation YamooPay`,
        confidence: 0.96,
        sources: [
          {
            title: 'B2B VAT Invoicing & Reverse Charge Compliance (Prompt 21)',
            slug: 'b2b-vat-invoicing-reverse-charge'
          }
        ],
        recommendedAction: 'Issue Stripe refund or credit note in Prompt 21 Billing Connector.',
        isHighRisk: true
      };
    }

    if (caseItem.domain === 'DEVELOPER_API') {
      return {
        suggestedReply: `Hello ${caseItem.customerName},\n\nRegarding Request ID ${caseItem.developerRequestId || 'req_yp_live_...'}, our gateway telemetry indicates that batch requests reached 25 req/sec while your Sandbox threshold was set to 20 req/sec. We have temporarily provisioned a 100 req/sec quota burst while your enterprise tier upgrade completes.\n\nBest regards,\nYamooPay Developer Relations`,
        confidence: 0.91,
        sources: [
          {
            title: 'Developer API: Understanding Rate Limits, HTTP 429 & Retry-After',
            slug: 'developer-api-rate-limits-and-best-practices'
          }
        ],
        recommendedAction: 'Verify Request ID in Prompt 20 Gateway logs and adjust API Key rate limits.',
        isHighRisk: false
      };
    }

    return {
      suggestedReply: `Hello ${caseItem.customerName},\n\nThank you for contacting YamooPay. We have reviewed your request and are happy to assist. Please let us know if you need any additional guidance.\n\nBest regards,\nYamooPay Support Team`,
      confidence: 0.85,
      sources: [
        {
          title: 'YamooPay Global Platform Guide & Navigation',
          slug: 'getting-started'
        }
      ],
      recommendedAction: 'General agent response.',
      isHighRisk: false
    };
  }

  // Getters
  public getQueues(): SupportQueue[] {
    return [...this.queues];
  }

  public getFinancialEscalations(): FinancialCalculationEscalation[] {
    return [...this.financialEscalations];
  }

  public getKnowledgeArticles(): KnowledgeArticle[] {
    return [...this.articles];
  }

  public getKnownIssues(): KnownIssue[] {
    return [...this.knownIssues];
  }

  public getKnowledgeGaps(): KnowledgeGap[] {
    return [...this.knowledgeGaps];
  }

  public getMacros(): SupportMacro[] {
    return [...this.macros];
  }

  public getVocThemes(): VoiceOfCustomerTheme[] {
    return [...this.vocThemes];
  }

  public getCsatResponses(): CSATResponse[] {
    return [...this.csatResponses];
  }

  public getNpsData(): NPSMetricData {
    return { ...this.npsData };
  }

  public getAuditLog(): SupportAuditEntry[] {
    return [...this.auditLog];
  }

  public recordCsat(score: 1 | 2 | 3 | 4 | 5, casePublicId: string, domain: SupportDomain, comment?: string): void {
    const entry: CSATResponse = {
      id: `csat_${Date.now()}`,
      casePublicId,
      customerUserId: 'usr_current',
      score,
      category: domain,
      market: 'GLOBAL',
      comment,
      createdAt: new Date().toISOString()
    };
    this.csatResponses.unshift(entry);
  }
}

export const supportEngine = new SupportEngineService();
